import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  ROTULO_DO_APONTAMENTO,
  formatarCronometro,
  formatarMinutos,
  hojeEmBrasilia,
  lerDuracao,
  useAcaoDoCronometro,
  useApagarApontamento,
  useApontarHoras,
  useHorasDoChamado,
  useIniciarCronometro,
  type Apontamento,
} from "../../nucleo/horas";
import { Aviso, Botao, Campo, Cartao, Chip } from "../ui";

interface Props {
  ticketId: string;
  /** Quem gerencia as horas também apaga o apontamento de outra pessoa. */
  podeGerenciar: boolean;
}

function quandoFoi(dia: string): string {
  const [ano, mes, diaDoMes] = dia.split("-");
  return `${diaDoMes}/${mes}/${ano}`;
}

/** O que o cronômetro mostra agora: o que o servidor contou mais o que passou desde a leitura. */
function segundosAgora(item: Apontamento, lidoEm: number, agora: number): number {
  if (item.estado !== "rodando") return item.segundos_decorridos;
  return item.segundos_decorridos + Math.max(0, Math.floor((agora - lidoEm) / 1000));
}

/** Horas do chamado (só a equipe): cronômetro, apontamento manual e a lista de quem trabalhou. */
export function HorasDoChamado({ ticketId, podeGerenciar }: Props) {
  const horas = useHorasDoChamado(ticketId);
  const apontar = useApontarHoras(ticketId);
  const iniciar = useIniciarCronometro(ticketId);
  const acao = useAcaoDoCronometro(ticketId);
  const apagar = useApagarApontamento(ticketId);

  const [duracao, setDuracao] = useState("");
  const [dia, setDia] = useState(hojeEmBrasilia());
  const [descricao, setDescricao] = useState("");
  const [consome, setConsome] = useState(true);
  const [erroDaDuracao, setErroDaDuracao] = useState<string | undefined>();
  const [agora, setAgora] = useState(() => Date.now());

  const meuCronometro = horas.data?.apontamentos.find((item) => item.meu && item.estado !== "concluido");
  const correndo = meuCronometro?.estado === "rodando";
  useEffect(() => {
    if (!correndo) return;
    const relogio = window.setInterval(() => setAgora(Date.now()), 1000);
    return () => window.clearInterval(relogio);
  }, [correndo]);

  function lancar(evento: FormEvent) {
    evento.preventDefault();
    const minutos = lerDuracao(duracao);
    if (minutos === null || minutos > 24 * 60) {
      setErroDaDuracao("Informe o tempo como 1h30, 90 (minutos) ou 1,5 (horas), até 24 horas.");
      return;
    }
    setErroDaDuracao(undefined);
    apontar.mutate(
      { minutos, dia, descricao: descricao.trim(), consome_contrato: consome },
      {
        onSuccess: () => {
          setDuracao("");
          setDescricao("");
        },
      },
    );
  }

  function ligar() {
    iniciar.mutate(
      { descricao: descricao.trim(), consome_contrato: consome },
      { onSuccess: () => setDescricao("") },
    );
  }

  const erro = apontar.error ?? iniciar.error ?? acao.error ?? apagar.error;

  return (
    <Cartao titulo="Horas trabalhadas" className="mb-4" data-testid="horas-do-chamado">
      <p className="nota-rodape mb-3">
        Só a equipe vê as horas. Elas saem do saldo do contrato de horas da empresa, quando há um vigente.
      </p>
      {horas.isPending ? (
        <p role="status">Carregando horas…</p>
      ) : horas.isError ? (
        <Aviso tom="erro">{mensagemDoConsole(horas.error)}</Aviso>
      ) : (
        <>
          <p className="cartao__texto mb-3" data-testid="total-de-horas">
            Total apontado: <strong>{formatarMinutos(horas.data.total_minutos)}</strong>
          </p>

          {meuCronometro ? (
            <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="meu-cronometro">
              <Chip estado={correndo ? "ok" : "atencao"} semPonto={!correndo}>
                {ROTULO_DO_APONTAMENTO[meuCronometro.estado]}
              </Chip>
              <strong aria-live="off" data-testid="mostrador">
                {formatarCronometro(segundosAgora(meuCronometro, horas.dataUpdatedAt, agora))}
              </strong>
              {correndo ? (
                <Botao
                  variante="fantasma"
                  tamanho="pequeno"
                  disabled={acao.isPending}
                  onClick={() => acao.mutate({ id: meuCronometro.id, acao: "pausar" })}
                >
                  Pausar
                </Botao>
              ) : (
                <Botao
                  variante="fantasma"
                  tamanho="pequeno"
                  disabled={acao.isPending}
                  onClick={() => acao.mutate({ id: meuCronometro.id, acao: "retomar" })}
                >
                  Retomar
                </Botao>
              )}
              <Botao
                tamanho="pequeno"
                disabled={acao.isPending}
                onClick={() => acao.mutate({ id: meuCronometro.id, acao: "parar" })}
              >
                Parar e apontar
              </Botao>
            </div>
          ) : null}

          <form className="flex flex-wrap items-end gap-3 mb-3" onSubmit={lancar} data-testid="apontar-horas">
            <Campo
              rotulo="Tempo"
              ajuda="Ex.: 1h30, 90 ou 1,5"
              value={duracao}
              onChange={(evento) => setDuracao(evento.target.value)}
              erro={erroDaDuracao}
              classeDaMoldura="flex-1"
              inputMode="text"
            />
            <Campo
              rotulo="Dia"
              type="date"
              value={dia}
              max={hojeEmBrasilia()}
              onChange={(evento) => setDia(evento.target.value)}
            />
            <Campo
              rotulo="O que foi feito"
              value={descricao}
              onChange={(evento) => setDescricao(evento.target.value)}
              classeDaMoldura="flex-1"
              maxLength={2000}
            />
            <label className="flex items-center gap-2 mb-2">
              <input type="checkbox" checked={consome} onChange={(evento) => setConsome(evento.target.checked)} />
              <span>Debitar do contrato</span>
            </label>
            <Botao type="submit" disabled={apontar.isPending}>
              {apontar.isPending ? "Apontando…" : "Apontar horas"}
            </Botao>
            {!meuCronometro ? (
              <Botao variante="fantasma" onClick={ligar} disabled={iniciar.isPending}>
                {iniciar.isPending ? "Iniciando…" : "Iniciar cronômetro"}
              </Botao>
            ) : null}
          </form>
          {erro ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(erro)}</Aviso> : null}

          {horas.data.apontamentos.length === 0 ? (
            <p className="nota-rodape">Ninguém apontou horas neste chamado ainda.</p>
          ) : (
            <ul className="lista-de-itens" data-testid="lista-de-apontamentos">
              {horas.data.apontamentos.map((item) => (
                <li className="flex flex-wrap items-center gap-2" key={item.id}>
                  <span>
                    <strong>{item.minutos === null ? "Em andamento" : formatarMinutos(item.minutos)}</strong>{" "}
                    · {item.agente_nome} · {quandoFoi(item.dia)}
                    {item.descricao ? ` · ${item.descricao}` : ""}
                  </span>
                  {item.origem === "cronometro" ? <Chip estado="neutro" semPonto>Cronômetro</Chip> : null}
                  {!item.consome_contrato ? <Chip estado="aguardando" semPonto>Fora do contrato</Chip> : null}
                  {item.estado !== "concluido" ? (
                    <Chip estado="atencao" semPonto>{ROTULO_DO_APONTAMENTO[item.estado]}</Chip>
                  ) : null}
                  {item.meu || podeGerenciar ? (
                    <Botao
                      variante="fantasma"
                      tamanho="pequeno"
                      aria-label={`Apagar apontamento de ${item.agente_nome} de ${quandoFoi(item.dia)}`}
                      disabled={apagar.isPending}
                      onClick={() => apagar.mutate(item.id)}
                    >
                      Apagar
                    </Botao>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Cartao>
  );
}
